import { UserPicker } from "../components/UserPicker";
import { ChevronDown, FileUp, LoaderCircle, Paperclip, Sparkles, Trash2, X } from "lucide-react";
import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  api, Department, Project, ProjectWorkflow, ProjectWorkflowExecution, Session, Task,
  StoredFile, TaskSchema, TaskSchemaField, TenantSettings, User, WorkItem,
} from "../api";
import { DepartmentOptions, orderedDepartments, userDepartmentIds, userDepartmentMemberships, userManagesDepartment, userManagesWithin, userWithinDepartment } from "../lib/org";
import { Avatar, ConfirmDialog } from "../components/ui";
import type { TaskSeed } from "../lib/types";

function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function TaskCompose({ session, seed, task, departments, users, projects, collaboration = false, collaborationPublishPolicy = "FREE", allowPastTaskDates = true, planned = false, createDraft = api.createTask, onClose, onCreated, onPersisted, onDeleted }: {
  session: Session; seed?: TaskSeed; task?: Task; departments: Department[]; users: User[]; projects: Project[];
  collaboration?: boolean; allowPastTaskDates?: boolean;
  planned?: boolean;
  createDraft?: (command: unknown) => Promise<Task>;
  collaborationPublishPolicy?: TenantSettings["collaborationPublishPolicy"];
  onClose: () => void; onCreated: (task: Task, message: string) => void; onPersisted?: (task: Task) => void;
  onDeleted?: (id: string) => void;
}) {
  const initial = task || seed?.copyFrom;
  const isCollaboration = collaboration || Boolean(initial?.collaborationSourceDepartmentId);
  const actor = users.find((user) => user.id === session.userId);
  const ownMemberships = actor ? userDepartmentMemberships(actor) : [];
  const primaryOwnDepartment = ownMemberships.find((membership) => membership.primary)?.departmentId
    || ownMemberships[0]?.departmentId;
  const initiallyManagedDepartment = departments.find((department) =>
    session.roles.includes("OWNER") || Boolean(actor && userManagesWithin(actor, department.id, departments)))?.id;
  const [sourceDepartmentId, setSourceDepartmentId] = useState(task?.collaborationSourceDepartmentId
    || (ownMemberships.some(m => m.departmentId === initial?.collaborationSourceDepartmentId) ? initial?.collaborationSourceDepartmentId : null) || primaryOwnDepartment || "");
  const [approverId, setApproverId] = useState(task?.collaborationApproverId || "");
  const sourceDepartments = departments.filter(department => department.status === "ACTIVE" && ownMemberships.some(m => m.departmentId === department.id));
  const sourceManager = session.roles.includes("OWNER") || Boolean(actor && userManagesDepartment(actor, sourceDepartmentId));
  const approvalRequired = isCollaboration && !sourceManager && collaborationPublishPolicy === "MANAGER_APPROVAL";
  const publishForbidden = isCollaboration && !sourceManager && collaborationPublishPolicy === "DISABLED";
  const approvers = users.filter(user => user.status === "ACTIVE" && user.id !== session.userId && userManagesDepartment(user, sourceDepartmentId));
  const selectedApprover = approvers.find(user => user.id === approverId);
  useEffect(() => {
    if (!isCollaboration) return;
    if (!approvers.some(user => user.id === approverId)) setApproverId(approvers.length === 1 ? approvers[0].id : "");
  }, [isCollaboration, sourceDepartmentId, users, approverId]);
  const [busy, setBusy] = useState(false);
  const [submitMode, setSubmitMode] = useState<"draft" | "publish">("draft");
  const [error, setError] = useState("");
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [schemas, setSchemas] = useState<TaskSchema[]>([]);

  const [internalTitle, setInternalTitle] = useState(initial?.internalTitle || "");
  const [priority, setPriority] = useState<Task["priority"]>(initial?.priority || "NORMAL");
  const [difficulty, setDifficulty] = useState<Task["difficulty"]>(initial?.difficulty || "MEDIUM");
  const [schemaVersionId, setSchemaVersionId] = useState(initial?.schemaVersionId || "");
  const [values, setValues] = useState<Record<string, string | number | boolean>>(() => editableCustomFields(initial));
  const [quantitativeGoal, setQuantitativeGoal] = useState(initial?.quantitativeGoal || "");
  const [verificationCriteria, setVerificationCriteria] = useState(initial?.verificationCriteria || "");
  const [testEnvironment, setTestEnvironment] = useState(initial?.testEnvironment || "");
  const [startsOn, setStartsOn] = useState(() => initial?.startsOn || localToday());
  const [dueOn, setDueOn] = useState(initial?.dueOn || "");
  const [background, setBackground] = useState(initial?.background || "");
  const [executionDetails, setExecutionDetails] = useState(initial?.executionDetails || "");
  const [deliverableCriteria, setDeliverableCriteria] = useState(initial?.deliverableCriteria || "");
  const [precautions, setPrecautions] = useState(initial?.precautions || "");
  const [privateTask, setPrivateTask] = useState(initial?.private || false);
  const [countsForEvaluation, setCountsForEvaluation] = useState(initial?.countsForEvaluation ?? true);
  const [exclusionReason, setExclusionReason] = useState(initial?.evaluationExclusionReason || "");
  const [suggesting, setSuggesting] = useState(false);
  const [assigneeRecommendation, setAssigneeRecommendation] = useState<{ userId: string; reason: string; random: boolean } | null>(null);
  const [persistedTaskId, setPersistedTaskId] = useState(task?.id || "");
  const [aiStatus, setAiStatus] = useState<Task["aiDraftStatus"]>(task?.aiDraftStatus);
  const [aiFailureReason, setAiFailureReason] = useState(task?.aiDraftFailureReason || "");
  const [aiProgress, setAiProgress] = useState(task?.aiDraftProgress || "");
  const [aiRequesting, setAiRequesting] = useState(false);
  const [aiCancelling, setAiCancelling] = useState(false);
  const cancelRequested = useRef(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [instructionAttachments, setInstructionAttachments] = useState<StoredFile[]>(initial?.instructionAttachments || []);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const excludedAiFiles = [...instructionAttachments.map(file => file.originalName), ...pendingFiles.map(file => file.name)]
    .filter(name => !/\.(pdf|docx|pptx|txt|md)$/i.test(name));
  const aiDrafting = aiRequesting || aiStatus === "QUEUED" || aiStatus === "GENERATING" || aiStatus === "CANCELLING";
  const cancelling = aiCancelling || aiStatus === "CANCELLING";
  const today = localToday();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  const [projectId, setProjectId] = useState(initial?.projectId || seed?.projectId || "");
  const [workflow, setWorkflow] = useState<ProjectWorkflow | undefined>();
  const [workflowExecution, setWorkflowExecution] = useState<ProjectWorkflowExecution | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [workItemId, setWorkItemId] = useState(initial?.workItemId || seed?.workItemId || "");
  const [departmentId, setDepartmentId] = useState(
    initial?.departmentId || seed?.departmentId || (isCollaboration ? "" : initiallyManagedDepartment || primaryOwnDepartment) || "",
  );
  const [targetDepartmentIds, setTargetDepartmentIds] = useState<string[]>(initial?.collaborationSourceDepartmentId ? [initial.departmentId] : []);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [teamQuery, setTeamQuery] = useState("");
  useEffect(() => {
    if (teamsOpen) document.getElementById("collaboration-team-options")?.scrollIntoView?.({ block: "nearest" });
  }, [teamsOpen]);
  const [assigneeIds, setAssigneeIds] = useState<string[]>(isCollaboration ? [] : initial?.assigneeIds || []);
  const [approvalManagerId, setApprovalManagerId] = useState(task?.approvalManagerId || "");

  const selectedProject = projects.find((project) => project.id === projectId);
  const isPm = session.roles.includes("OWNER") || selectedProject?.members.some((member) => member.userId === session.userId && member.role === "PM");
  // 하위 업무는 상위 업무에 붙는다. 고를 수 있는 상위 업무는 진행 중인 단계의 것,
  // 그리고 내가 그 업무를 맡았거나 그 팀의 관리자이거나 PM 인 경우다.
  const activeStageIds = new Set([...(workflowExecution?.runs || []), ...(workflowExecution?.archivedRuns || [])].filter((run) => run.status === "ACTIVE").map((run) => run.workflowStageId));
  const allowedWorkItems = workItems.filter((item) => item.status !== "CANCELLED"
    && (activeStageIds.has(item.workflowStageId) || item.id === task?.workItemId || item.id === seed?.workItemId)
    && (isPm || item.currentOwnerId === session.userId
      || (actor && session.roles.includes("MANAGER") && userManagesWithin(actor, item.currentDepartmentId, departments))));
  const selectedWorkItem = workItems.find((item) => item.id === workItemId);
  const canAssignOthers = !isCollaboration && (session.roles.includes("OWNER") || isPm
    || Boolean(actor && departmentId && userManagesWithin(actor, departmentId, departments)));
  const selfAssignmentOnly = !isCollaboration && Boolean(actor && departmentId) && !canAssignOthers;
  const isProposal = !isCollaboration && (selfAssignmentOnly || assigneeIds.includes(session.userId));
  const reviewingProposal = task?.status === "REQUESTED" && !isProposal;
  const approvalManagers = users.filter(user => user.status === "ACTIVE" && !assigneeIds.includes(user.id)
    && (userManagesDepartment(user, departmentId) || (user.roles.includes("OWNER") && userDepartmentIds(user).includes(departmentId))));
  useEffect(() => {
    if (!isProposal) return;
    if (approvalManagers.some(user => user.id === approvalManagerId)) return;
    const defaultId = departments.find(department => department.id === departmentId)?.defaultManagerId;
    setApprovalManagerId(approvalManagers.find(user => user.id === defaultId)?.id
      || (approvalManagers.length === 1 ? approvalManagers[0].id : ""));
  }, [isProposal, departmentId, approvalManagerId, approvalManagers.map(user => user.id).join(","), departments]);
  const stageNameOf = (item: WorkItem) => workflow?.stages.find((stage) => stage.id === item.workflowStageId)?.name || "단계";
  const creatableProjects = projects.filter((project) => project.status === "ACTIVE" && (
    session.roles.includes("OWNER")
      || project.members.some((member) => member.userId === session.userId && member.role === "PM")
      || ((initial?.projectId === project.id || seed?.projectId === project.id) && Boolean(initial?.workItemId || seed?.workItemId || seed?.workflowStageKey))
  ));
  const selectableDepartments = useMemo(() => {
    if (isCollaboration) {
      const ownDepartments = new Set(actor ? userDepartmentIds(actor) : []);
      return departments.filter((department) => department.status === "ACTIVE" && !ownDepartments.has(department.id));
    }
    if (seed?.workflowStageKey) return departments.filter((department) => (seed.workflowDepartmentIds || [seed.departmentId]).includes(department.id)
      && department.status === "ACTIVE" && (session.roles.includes("OWNER") || isPm || Boolean(actor && userManagesWithin(actor, department.id, departments))));
    if (selectedWorkItem?.currentDepartmentId) return departments.filter((department) => department.id === selectedWorkItem.currentDepartmentId);
    if (projectId && isPm) {
      const participating = new Set(selectedProject?.participatingDepartmentIds || []);
      return participating.size > 0 ? departments.filter((department) => participating.has(department.id)) : departments;
    }
    const ownDepartments = new Set(actor ? userDepartmentIds(actor) : []);
    return departments.filter((department) => department.status === "ACTIVE" && (session.roles.includes("OWNER")
      || ownDepartments.has(department.id)
      || Boolean(actor && userManagesWithin(actor, department.id, departments))));
  }, [actor, departments, isCollaboration, isPm, projectId, selectedProject?.participatingDepartmentIds, selectedWorkItem, session.roles, seed?.workflowStageKey, seed?.workflowDepartmentIds, seed?.departmentId]);
  const selectableDepartmentKey = selectableDepartments.map((department) => department.id).join(",");

  useEffect(() => {
    void api.taskConfiguration().then((configuration) => {
      const tenantSchemas = configuration.schemas.filter((schema) => schema.scopeType === "TENANT"
        && schema.status === "ACTIVE" && schema.currentVersion.status === "PUBLISHED");
      const defaultSchema = tenantSchemas.find((schema) => schema.id === configuration.defaultSchemaId) || tenantSchemas[0];
      setSchemas(tenantSchemas);
      if (!initial?.schemaVersionId || (seed?.copyFrom && !tenantSchemas.some(schema => schema.currentVersion.id === initial.schemaVersionId))) setSchemaVersionId(defaultSchema?.currentVersion.id || "");
    }).catch(() => undefined);
  }, [task?.schemaVersionId]);
  useEffect(() => {
    if (!projectId) { setWorkflow(undefined); setWorkflowExecution(null); setWorkItems([]); if (!seed?.workItemId && !task?.workItemId) setWorkItemId(""); return; }
    void Promise.all([api.projectWorkflow(projectId), api.projectWorkflowExecution(projectId), api.projectWorkItems(projectId)])
      .then(([nextWorkflow, nextExecution, nextWorkItems]) => {
        setWorkflow(nextWorkflow); setWorkflowExecution(nextExecution); setWorkItems(nextWorkItems);
      }).catch((reason) => setError(reason instanceof Error ? reason.message : "프로젝트 업무를 불러오지 못했습니다."));
  }, [projectId]);
  useEffect(() => {
    // 업무를 고르면 부서가 따라온다. 담당자는 그 팀 안에서만 고를 수 있다.
    if (!selectedWorkItem) return;
    if (!seed?.workflowStageKey && selectedWorkItem.currentDepartmentId) setDepartmentId(selectedWorkItem.currentDepartmentId);
  }, [selectedWorkItem]);
  useEffect(() => {
    if (!selfAssignmentOnly) return;
    setAssigneeIds([session.userId]);

    setAssigneeRecommendation(null);
  }, [selfAssignmentOnly, session.userId]);
  useEffect(() => {
    if (isCollaboration) { setDepartmentId(targetDepartmentIds[0] || ""); return; }
    if (selectedWorkItem || selectableDepartments.some((department) => department.id === departmentId)) return;
    setDepartmentId(selectableDepartments[0]?.id || "");
    setAssigneeIds([]); setAssigneeRecommendation(null);
  }, [departmentId, selectableDepartmentKey, selectedWorkItem, isCollaboration, targetDepartmentIds]);

  const projectMemberIds = new Set(selectedProject?.members.map((member) => member.userId) || []);
  const activeUsers = users.filter((user) => user.status === "ACTIVE" && (
    selectedWorkItem ? userWithinDepartment(user, selectedWorkItem.currentDepartmentId, departments)
      : projectId ? projectMemberIds.has(user.id)
        : Boolean(departmentId && userWithinDepartment(user, departmentId, departments))
  ));
  const selectedAssignee = users.find((user) => user.id === assigneeIds[0]);

  function selectAssignee(userId: string, recommendation: typeof assigneeRecommendation = null) {
    setAssigneeIds([userId]);
    setAssigneeRecommendation(recommendation);

  }

  function selectRandomAssignee() {
    const picked = activeUsers[Math.floor(Math.random() * activeUsers.length)];
    if (!picked) return;
    selectAssignee(picked.id, {
      userId: picked.id,
      reason: "추천 근거가 없어 선택 가능한 담당자 중 무작위로 선택했습니다.",
      random: true,
    });
  }

  async function suggestAssignees() {
    if (!departmentId) { setError("담당 부서를 먼저 선택하세요."); return; }
    if (activeUsers.length === 0) { setError("선택할 수 있는 담당자가 없습니다."); return; }
    setSuggesting(true); setError("");
    try {
      const eligibleIds = new Set(activeUsers.map((user) => user.id));
      const suggestions = await api.assigneeSuggestions(departmentId);
      const grounded = suggestions.find((item) => eligibleIds.has(item.userId) && item.reason.trim());
      if (grounded) {
        selectAssignee(grounded.userId, { userId: grounded.userId, reason: grounded.reason.trim(), random: false });
      } else {
        selectRandomAssignee();
      }
    }
    catch { selectRandomAssignee(); }
    finally { setSuggesting(false); }
  }

  function applyTask(next: Task) {
    setInternalTitle(next.internalTitle); setBackground(next.background);
    setExecutionDetails(next.executionDetails); setDeliverableCriteria(next.deliverableCriteria);
    setPrecautions(next.precautions); setPriority(next.priority);
    setDifficulty(next.difficulty); setPrivateTask(isCollaboration ? false : next.private);
    setAssigneeIds(isCollaboration ? [] : selfAssignmentOnly ? [session.userId] : next.assigneeIds);
    setStartsOn(next.startsOn || ""); setDueOn(next.dueOn || "");
    setQuantitativeGoal(next.quantitativeGoal || "");
    setVerificationCriteria(next.verificationCriteria || ""); setTestEnvironment(next.testEnvironment || "");
    setCountsForEvaluation(next.countsForEvaluation); setExclusionReason(next.evaluationExclusionReason || "");
    setValues(editableCustomFields(next));
    setInstructionAttachments(next.instructionAttachments || []);
    setAiStatus(next.aiDraftStatus); setAiFailureReason(next.aiDraftFailureReason || "");
  }

  useEffect(() => {
    if (!persistedTaskId || !["QUEUED", "GENERATING", "CANCELLING"].includes(aiStatus || "")) return;
    let stopped = false;
    const refresh = async () => {
      try {
        const next = await api.task(persistedTaskId);
        if (stopped) return;
        setAiStatus(current => cancelRequested.current && ["QUEUED", "GENERATING"].includes(next.aiDraftStatus || "") ? current : next.aiDraftStatus);
        setAiFailureReason(next.aiDraftFailureReason || "");
        setAiProgress(next.aiDraftProgress || "");
        if (next.aiDraftStatus === "SUCCEEDED" && !cancelRequested.current) applyTask(next);
        if (next.aiDraftStatus === "FAILED") setError(next.aiDraftFailureReason || "AI 초안을 만들지 못했습니다.");
        onPersisted?.(next);
      } catch { /* 다음 폴링에서 다시 확인한다. */ }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 2000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [persistedTaskId, aiStatus]);

  function draftCommand() {
    return {
      departmentId, internalTitle, priority, difficulty,
      startsOn: startsOn || null, dueOn: dueOn || null, private: isCollaboration ? false : privateTask,
      assigneeIds: isCollaboration ? [] : selfAssignmentOnly ? [session.userId] : assigneeIds,
      background, executionDetails, deliverableCriteria, precautions,
      countsForEvaluation, evaluationExclusionReason: countsForEvaluation ? null : exclusionReason || null,
      projectId: isCollaboration ? null : projectId || null, workItemId: isCollaboration ? null : workItemId || null,
      workflowStageKey: seed?.workflowStageKey,
      schemaVersionId: schemaVersionId || null,
      customFields: Object.fromEntries(schemaFields
        .filter((field) => !(field.key in bespokeFields) && values[field.key] !== undefined && values[field.key] !== "")
        .map((field) => [field.key, values[field.key]])),
      quantitativeGoal: quantitativeGoal || null, verificationCriteria: verificationCriteria || null, testEnvironment: testEnvironment || null,
      collaboration: isCollaboration,
      ...(isCollaboration ? { collaborationSourceDepartmentId: sourceDepartmentId || null, collaborationApproverId: approvalRequired ? selectedApprover?.id || null : null } : {}),
      approvalManagerId: approvalManagers.some(user => user.id === approvalManagerId) ? approvalManagerId : null,
      ...(!persistedTaskId && seed?.copyFrom ? { copyFromTaskId: seed.copyFrom.id, copyAttachmentIds: instructionAttachments.map(file => file.id) } : {}),
    };
  }

  function queueFiles(files: FileList | null) {
    if (!files?.length) return;
    if (Array.from(files).some(file => file.size === 0 || file.size > 25 * 1024 * 1024)) {
      setError("첨부파일은 비어 있지 않은 25MB 이하 파일을 선택해 주세요."); return;
    }
    setPendingFiles((current) => {
      const known = new Set(current.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      return [...current, ...Array.from(files).filter((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(key)) return false;
        known.add(key);
        return true;
      })];
    });
  }

  async function persistPendingFiles(base: Task, forAi = false): Promise<Task> {
    if (pendingFiles.length === 0) return { ...base, instructionAttachments: base.instructionAttachments || instructionAttachments };
    setAttachmentBusy(true);
    const attached = [...(base.instructionAttachments || instructionAttachments)];
    try {
      for (const file of [...pendingFiles]) {
        if (forAi && cancelRequested.current) break;
        setAiProgress(`중복 확인 중 · ${file.name}`);
        const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
        const hash = Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2, "0")).join("");
        if (forAi && cancelRequested.current) break;
        const duplicate = await api.lookupFile(hash, file.size);
        if (forAi && cancelRequested.current) break;
        setAiProgress(`${duplicate.file ? "기존 파일 재사용" : "업로드 중"} · ${file.name}`);
        const stored = duplicate.file || await api.uploadFile(file);
        await api.attachTaskInstructionFile(stored.id, base.id);
        if (!attached.some(item => item.id === stored.id)) attached.push(stored);
        setInstructionAttachments([...attached]);
        setPendingFiles((current) => current.filter((candidate) => candidate !== file));
      }
      return { ...base, instructionAttachments: attached };
    } finally {
      setAttachmentBusy(false);
    }
  }

  async function removeInstructionAttachment(fileId: string) {
    if (!persistedTaskId) { setInstructionAttachments(current => current.filter(file => file.id !== fileId)); return; }
    setAttachmentBusy(true); setError("");
    try {
      await api.detachTaskInstructionFile(fileId, persistedTaskId);
      setInstructionAttachments((current) => current.filter((file) => file.id !== fileId));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "첨부 파일을 삭제하지 못했습니다.");
    } finally { setAttachmentBusy(false); }
  }

  async function generateAiDraft() {
    const issues = draftValidationErrors();
    if (issues.length) { showValidationErrors(issues, "AI 초안을 만들기 위한 필수 항목을 확인해 주세요."); return; }
    const filenames = [...instructionAttachments.map(file => file.originalName), ...pendingFiles.map(file => file.name)];
    if (filenames.length > 10) { setError("AI 분석은 첨부파일을 최대 10개까지 지원합니다."); return; }
    const previousAiStatus = aiStatus;
    cancelRequested.current = false;
    setAiRequesting(true); setAiFailureReason(""); setError(""); setAiProgress("업무 초안 저장 중");
    try {
      const saved = persistedTaskId
        ? await api.updateTask(persistedTaskId, draftCommand())
        : await createDraft(draftCommand());
      setPersistedTaskId(saved.id);
      if (saved.workItemId) setWorkItemId(saved.workItemId);
      const savedWithFiles = await persistPendingFiles(saved, true);
      onPersisted?.(savedWithFiles);
      if (cancelRequested.current) { setAiStatus("CANCELLED"); return; }
      setAiProgress("문서 분석 대기 중");
      const queued = await api.requestTaskAiDraft(saved.id);
      setPersistedTaskId(queued.id); setAiStatus(queued.aiDraftStatus || "QUEUED");
      onPersisted?.(queued);
      if (cancelRequested.current) await cancelGeneration(queued.id, true);
    } catch (reason) {
      setAiStatus(previousAiStatus); setError(reason instanceof Error ? reason.message : "AI 초안을 요청하지 못했습니다.");
    } finally { setAiRequesting(false); setAiCancelling(false); }
  }

  async function cancelGeneration(id = persistedTaskId, queued = false) {
    cancelRequested.current = true;
    setAiCancelling(true); setError("");
    if (aiRequesting && !queued) return;
    try {
      const next = await api.cancelTaskAiDraft(id);
      setAiStatus(next.aiDraftStatus); setAiProgress(next.aiDraftProgress || ""); onPersisted?.(next);
    } catch (reason) {
      cancelRequested.current = false;
      setError(reason instanceof Error ? reason.message : "AI 생성을 취소하지 못했습니다.");
    } finally { setAiCancelling(false); }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const intent = !planned && ((event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null)?.value === "publish" ? "publish" : "draft";
    const issues = [...draftValidationErrors(), ...(intent === "publish" || planned ? publishValidationErrors() : [])]
      .filter((issue, index, all) => all.findIndex((candidate) => candidate.key === issue.key) === index);
    if (issues.length > 0) {
      setSubmitMode(intent); showValidationErrors(issues, `필수 항목을 확인해 주세요: ${issues.map((item) => item.label).join(" · ")}`);
      return;
    }
    setInvalidFields(new Set());
    setSubmitMode(intent); setBusy(true); setError("");
    try {
      const existing = Boolean(persistedTaskId);
      const created = existing ? await api.updateTask(persistedTaskId, draftCommand()) : await createDraft(draftCommand());
      setPersistedTaskId(created.id);
      if (created.workItemId) setWorkItemId(created.workItemId);
      const createdWithFiles = await persistPendingFiles(created);
      if (isCollaboration && targetDepartmentIds.length > 1) {
        onPersisted?.(createdWithFiles);
        const distributed = await api.distributeCollaborationTask(created.id, targetDepartmentIds, intent === "publish");
        distributed.forEach(item => onPersisted?.(item));
        onCreated(distributed[0], `${distributed.length}개 팀에 협업 요청${intent === "draft" ? " 초안을 저장했습니다." : distributed[0].status === "APPROVAL_PENDING" ? "의 발신 승인을 요청했습니다." : "을 발행했습니다."}`);
        return;
      }
      if (intent === "publish" && task?.status === "REQUESTED" && isProposal) {
        onCreated(createdWithFiles, "승인 요청을 저장했습니다. 다른 관리자가 승인하면 업무를 시작할 수 있습니다.");
        return;
      }
      if (intent === "publish") {
        try {
          const published = isCollaboration ? await api.publishCollaborationTask(created.id) : await api.taskAction(created.id, "confirm");
          onCreated(published, isCollaboration
            ? published.status === "APPROVAL_PENDING" ? "발신 팀 매니저에게 승인을 요청했습니다." : task?.status === "REJECTED" ? "협업 요청을 수정해 다시 보냈습니다." : "협업 요청을 발행했습니다."
            : isProposal ? "업무를 제안했습니다. 매니저가 승인하면 준비 업무로 전환됩니다." : "지시서를 발행했습니다.");
        } catch (reason) {
          onPersisted?.(createdWithFiles);
          setError(`내용은 저장됐지만 ${reviewingProposal ? "승인" : "발행"}하지 못했습니다. ${reason instanceof Error ? reason.message : "다시 시도해 주세요."}`);
        }
      } else onCreated(createdWithFiles, existing
        ? isCollaboration ? "협업 요청 초안을 수정했습니다." : isProposal ? "업무 제안 초안을 수정했습니다." : "업무 초안을 수정했습니다."
        : isCollaboration ? "협업 요청 초안을 저장했습니다." : isProposal ? "업무 제안 초안을 저장했습니다." : "업무 초안을 저장했습니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : isCollaboration ? "협업 요청을 만들지 못했습니다." : "업무를 만들지 못했습니다."); }
    finally { setBusy(false); }
  }

  function showValidationErrors(issues: { key: string; label: string }[], message: string) {
    setInvalidFields(new Set(issues.map((item) => item.key))); setError(message);
    window.requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(`[data-compose-field="${issues[0].key}"]`);
      target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      const control = target?.matches("input, select, textarea, button") ? target : target?.querySelector<HTMLElement>("input, select, textarea, button");
      control?.focus({ preventScroll: true });
    });
  }

  const selectedSchema = schemas.find((schema) => schema.currentVersion.id === schemaVersionId);
  const fieldRequired = (key: string) => selectedSchema?.currentVersion.fields
    .some((field) => field.active && field.key === key && field.required) || false;
  /** 선택한 양식의 활성 항목 순서대로 그린다. */
  const schemaFields = [...(selectedSchema?.currentVersion.fields || [])]
    .filter((field) => field.active && field.key !== "background"
      && (!isCollaboration || !["project", "assignee", "private"].includes(field.key)))
    .sort((left, right) => left.sortOrder - right.sortOrder);

  function fieldHasValue(key: string): boolean {
    switch (key) {
      case "project": return Boolean(projectId);
      case "sourceDepartment": return Boolean(sourceDepartmentId);
      case "approver": return Boolean(selectedApprover);
      case "importance": case "difficulty": case "private": return true;
      case "schedule": return Boolean(startsOn && dueOn && dueOn >= startsOn && (allowPastTaskDates || startsOn >= today));
      case "assignee": return selfAssignmentOnly || assigneeIds.length === 1;
      case "background": return Boolean(background.trim());
      case "execution": return Boolean(executionDetails.trim());
      case "quantitative_goal": return Boolean(quantitativeGoal.trim());
      case "verification": return Boolean(verificationCriteria.trim());
      case "deliverable": return Boolean(deliverableCriteria.trim());
      case "precautions": return Boolean(precautions.trim());
      case "attachments": return instructionAttachments.length + pendingFiles.length > 0;
      default: {
        const value = values[key];
        return value !== undefined && value !== null && value !== "";
      }
    }
  }

  function publishValidationErrors(): { key: string; label: string }[] {
    const missing: { key: string; label: string }[] = [];
    if (isCollaboration && !sourceDepartments.some(d => d.id === sourceDepartmentId)) missing.push({ key: "sourceDepartment", label: "발신 팀" });
    if (approvalRequired && !selectedApprover) missing.push({ key: "approver", label: "승인 매니저" });
    if (publishForbidden) missing.push({ key: "sourceDepartment", label: "일반 구성원의 협업 발행 불가 정책" });
    if (isProposal && !approvalManagerId) missing.push({ key: "approvalManager", label: "승인 요청할 관리자" });
    if (!schemaVersionId || !selectedSchema) missing.push({ key: "schema", label: "양식" });
    if (!internalTitle.trim()) missing.push({ key: "internalTitle", label: "업무 이름" });
    if (!departmentId) missing.push({ key: "department", label: isCollaboration ? "요청할 팀" : "부서" });
    selectedSchema?.currentVersion.fields
      .filter((field) => field.active && field.required
        && !(isCollaboration && ["assignee", "schedule", "project", "private"].includes(field.key))
        && !fieldHasValue(field.key))
      .forEach((field) => missing.push({ key: field.key, label: field.label.replace(/^\[|\]$/g, "") }));
    if ((!isCollaboration && (!startsOn || !dueOn || dueOn < startsOn)) || (!allowPastTaskDates && [startsOn, dueOn].some((date) => date && date < today))) {
      if (!missing.some((item) => item.key === "schedule")) missing.push({ key: "schedule", label: "일정" });
    }
    if (!isCollaboration && assigneeIds.length !== 1) {
      if (!missing.some((item) => item.key === "assignee")) missing.push({ key: "assignee", label: "담당자" });
    }
    if (!countsForEvaluation && !exclusionReason.trim()) missing.push({ key: "evaluation", label: "평가 제외 사유" });
    return missing;
  }

  function draftValidationErrors(): { key: string; label: string }[] {
    const missing: { key: string; label: string }[] = [];
    if (!internalTitle.trim()) missing.push({ key: "internalTitle", label: "업무 제목" });
    if (!background.trim()) missing.push({ key: "background", label: "업무 설명" });
    if (!schemaVersionId || !selectedSchema) missing.push({ key: "schema", label: "양식" });
    if (!departmentId || (isCollaboration && (targetDepartmentIds.length > 50 || targetDepartmentIds.some(id => !selectableDepartments.some(d => d.id === id))))) missing.push({ key: "department", label: isCollaboration ? "요청할 팀 (활성 팀 1~50개)" : "부서" });
    if (!countsForEvaluation && !exclusionReason.trim()) missing.push({ key: "evaluation", label: "평가 제외 사유" });
    return missing;
  }

  const fieldInvalid = (key: string) => invalidFields.has(key) && !fieldHasValue(key);
  const bespokeFields: Record<string, { ai: boolean; node: ReactNode }> = {
    project: { ai: false, node: <>
      <select aria-label="프로젝트" value={projectId} disabled={Boolean(task?.proposerId || seed?.workflowStageKey || task?.workItemId)} onChange={(event) => { setProjectId(event.target.value); setWorkItemId(""); setAssigneeIds([]); setAssigneeRecommendation(null); }}>
      <option value="">프로젝트 없음 · 단독 업무</option>
      {creatableProjects.map((project) => <option value={project.id} key={project.id}>{project.code} · {project.name}</option>)}
      </select>
    </> },
    importance: { ai: true, node: <>
      <div className="compose-inline">
      <select aria-label="우선순위" value={priority} onChange={(event) => setPriority(event.target.value as Task["priority"])}>
      <option value="LOW">낮음</option><option value="NORMAL">보통</option><option value="HIGH">높음</option><option value="URGENT">긴급</option>
      </select>
      </div>
    </> },
    difficulty: { ai: true, node: <>
      <select aria-label="난이도" value={difficulty} onChange={(event) => setDifficulty(event.target.value as Task["difficulty"])}>
      <option value="LOW">하</option><option value="MEDIUM">중</option><option value="HIGH">상</option>
      </select>
      <small>상 = 신규 기획·외부 협업 포함 / 중 = 기존 절차 안의 판단 / 하 = 단순 반복 업무</small>
    </> },
    schedule: { ai: false, node: <>
      <div className="compose-schedule">
      <input aria-label="시작일" type="date" min={allowPastTaskDates ? undefined : today} value={startsOn} onChange={(event) => setStartsOn(event.target.value)} />
      <span>→</span>
      <input aria-label="마감일" type="date" min={startsOn || (allowPastTaskDates ? undefined : today)} value={dueOn} onChange={(event) => setDueOn(event.target.value)} />
      <small>{allowPastTaskDates ? "초안에서는 비워둘 수 있습니다. 시작일과 마감일은 발행 전에 지정해야 합니다." : "조직 정책에 따라 업무 발행일 이전의 시작일·마감일은 선택할 수 없습니다."}</small>
      </div>
    </> },
    assignee: { ai: false, node: selfAssignmentOnly ? <div className="compose-assignees">
      {actor && <div className="assignee-selected self-assigned">
        <Avatar name={actor.displayName} userId={actor.id} avatarVersion={actor.avatarVersion} size="sm" />
        <span><b>{actor.displayName}</b><small>{actor.jobTitle || "구성원"} · 본인 담당</small></span>
      </div>}
      <small>다른 사람에게 할당할 수 없으며, 매니저가 제안을 승인하면 본인 업무로 배정됩니다.</small>
    </div> : <>
      <div className="compose-assignees">
      <div className="assignee-toolbar">
        <span>{selectedWorkItem ? "이 업무를 맡은 팀에서 검색하세요." : "이름, 직책 또는 이메일로 담당자를 검색하세요."}</span>
        <button type="button" disabled={suggesting || !departmentId || activeUsers.length === 0} onClick={() => void suggestAssignees()}><Sparkles size={14} />{suggesting ? "분석 중…" : "추천 보기"}</button>
      </div>
      <UserPicker key={departmentId} label="담당자" users={activeUsers} value={assigneeIds} onChange={ids => { setAssigneeIds(ids); setAssigneeRecommendation(null); }} />
      {assigneeRecommendation && selectedAssignee && assigneeRecommendation.userId === selectedAssignee.id && <div className={`assignee-recommendation${assigneeRecommendation.random ? " random" : ""}`}>
        <Sparkles size={16} />
        <div><b>{assigneeRecommendation.random ? "선택 안내" : "추천 사유"}</b><p>{assigneeRecommendation.reason}</p></div>
      </div>}
      </div>
    </> },
    execution: { ai: true, node: <>
      <textarea aria-label="수행 내용" value={executionDetails} onChange={(event) => setExecutionDetails(event.target.value)} required={fieldRequired("execution")} rows={4} placeholder="무엇을, 어디까지 수행해야 하나요?" />
    </> },
    quantitative_goal: { ai: false, node: <>
      <textarea aria-label="정량 목표" value={quantitativeGoal} onChange={(event) => setQuantitativeGoal(event.target.value)} required={fieldRequired("quantitative_goal")} rows={2} placeholder="수치로 표현된 달성 목표 (예: 추천 상위 3인 적중률 90% 이상)" />
    </> },
    verification: { ai: false, node: <>
      <textarea aria-label="검증 기준" value={verificationCriteria} onChange={(event) => setVerificationCriteria(event.target.value)} required={fieldRequired("verification")} rows={2} placeholder="무엇을 확인하면 완료인가" />
      <textarea aria-label="시험 환경" value={testEnvironment} onChange={(event) => setTestEnvironment(event.target.value)} rows={2} placeholder="어떤 환경에서 확인하는가 (예: 스테이징 3개 부서 실데이터)" />
    </> },
    deliverable: { ai: true, node: <>
      <textarea aria-label="산출물 기준" value={deliverableCriteria} onChange={(event) => setDeliverableCriteria(event.target.value)} required={fieldRequired("deliverable")} rows={3} placeholder="어떤 상태가 되면 완료인가요?" />
    </> },
    precautions: { ai: true, node: <>
      <textarea aria-label="주의사항" value={precautions} onChange={(event) => setPrecautions(event.target.value)} required={fieldRequired("precautions")} rows={2} placeholder="업무 수행 시 반드시 지켜야 할 조건" />
    </> },
    attachments: { ai: false, node: <div className="compose-attachments">
      <label className="compose-file-picker">
        <span><FileUp size={17} /><b>파일 선택</b><small>파일당 최대 25 MB · AI 초안은 첨부 최대 10개 · PDF, DOCX, PPTX, TXT, MD 분석</small></span>
        <input className="compose-file-input" aria-label="첨부 파일 선택" type="file" multiple disabled={busy || attachmentBusy || aiDrafting}
          onChange={(event) => { queueFiles(event.target.files); event.target.value = ""; }} />
      </label>
      <small>첨부 문서의 텍스트와 표를 AI 초안에 활용합니다. TXT·MD는 UTF-8 또는 BOM이 있는 UTF-16을 지원합니다. 이미지·차트 해석과 스캔 PDF는 지원하지 않습니다.</small>
      {excludedAiFiles.length > 0 && <p className="permission-note" role="status">AI 분석 제외: {excludedAiFiles.join(", ")}. 미지원 형식은 첨부로 유지하고, 지원 문서와 입력한 업무 내용·기존 자료로 초안을 생성합니다.</p>}
      {(instructionAttachments.length > 0 || pendingFiles.length > 0) && <div className="compose-file-list">
        {instructionAttachments.map((file) => <div key={file.id}>
          <Paperclip size={15} /><span><b>{file.originalName}</b><small>{fileSize(file.sizeBytes)} · 업로드됨</small></span>
          <button type="button" aria-label={`${file.originalName} 삭제`} disabled={busy || attachmentBusy || aiDrafting}
            onClick={() => void removeInstructionAttachment(file.id)}><Trash2 size={15} /></button>
        </div>)}
        {pendingFiles.map((file) => <div key={`${file.name}:${file.size}:${file.lastModified}`}>
          <Paperclip size={15} /><span><b>{file.name}</b><small>{fileSize(file.size)} · 저장할 때 업로드</small></span>
          <button type="button" aria-label={`${file.name} 제거`} disabled={busy || attachmentBusy || aiDrafting}
            onClick={() => setPendingFiles((current) => current.filter((candidate) => candidate !== file))}><X size={15} /></button>
        </div>)}
      </div>}
    </div> },
    private: { ai: false, node: <>
      <label className="compose-check"><input type="checkbox" checked={privateTask} disabled={Boolean(seed?.workflowStageKey || workItemId)} onChange={(event) => setPrivateTask(event.target.checked)} />{seed?.workflowStageKey || workItemId ? "Work에는 공개 업무만 연결할 수 있습니다" : "같은 부서 또는 프로젝트 구성원만 열람"}</label>
    </> },
  };

  return <div className="task-compose-layer"><button className="task-compose-backdrop" onClick={onClose} aria-label="닫기" /><section className="task-compose-modal">
    <header><h2>{isCollaboration ? persistedTaskId ? "협업 요청 초안 수정" : "협업 요청 만들기" : isProposal ? persistedTaskId ? "업무 제안 수정" : "업무 제안" : persistedTaskId ? "업무 초안 수정" : seed?.workItemId ? "하위 업무 생성" : "업무 생성"}</h2><div><span className="draft-badge">{isProposal ? "제안 초안" : "초안"}</span><button type="button" onClick={onClose} aria-label="닫기"><X size={17} /></button></div></header>
    <form onSubmit={submit} noValidate>
      <div className="task-compose-body">
        {planned && <p className="muted" role="status">예정 업무 · 담당자와 수행 내용을 미리 지정합니다. 지금은 발행하지 않고 Work가 해당 단계에 도달하면 기존 승인 정책에 따라 발행합니다.</p>}
        {seed?.copyFrom && <p className="muted" role="status">업무 복제 · 지시서와 선택한 첨부파일을 새 업무로 저장합니다. 수행 결과·평가·승인 이력은 복사하지 않습니다. 담당자와 일정을 확인해 주세요.</p>}
        <section className="task-compose-brief">
          <label className="compose-brief-label" htmlFor="task-compose-title">업무 제목 <i title="필수 항목">*</i></label>
          <input id="task-compose-title" disabled={aiDrafting || busy} data-compose-field="internalTitle" className={invalidFields.has("internalTitle") && !internalTitle.trim() ? "compose-invalid" : ""} aria-label="업무 이름" value={internalTitle} onChange={(event) => setInternalTitle(event.target.value)} required maxLength={240} autoFocus placeholder="제목을 입력하세요" />
          <label className="compose-brief-label" htmlFor="task-compose-background">업무 설명 <i title="필수 항목">*</i></label>
          <textarea id="task-compose-background" disabled={aiDrafting || busy} data-compose-field="background" className={fieldInvalid("background") ? "compose-invalid" : ""} aria-label="업무 배경과 목적" value={background} onChange={(event) => setBackground(event.target.value)} required rows={3} placeholder="왜 이 업무가 필요한지 적어주세요." />
          {isCollaboration && task?.collaborationRejectionReason && <div className="collaboration-rejection"><b>반려 사유</b><p>{task.collaborationRejectionReason}</p></div>}
          <div className={`task-form-choice${invalidFields.has("schema") && !selectedSchema ? " compose-invalid" : ""}`} data-compose-field="schema">
            <label htmlFor="task-form-select"><b>양식 <i title="필수 항목">*</i></b><span>AI와 직접 작성에 같은 항목 구성을 적용합니다.</span></label>
            <select id="task-form-select" disabled={aiDrafting || busy} aria-label="업무 양식" value={schemaVersionId} onChange={(event) => setSchemaVersionId(event.target.value)}>
              <option value="">양식 선택</option>
              {schemas.map((schema) => <option value={schema.currentVersion.id} key={schema.id}>{schema.name}</option>)}
            </select>
          </div>
          <ComposeField label="첨부파일" fieldKey="attachments" required={fieldRequired("attachments")} invalid={fieldInvalid("attachments")}>
            {bespokeFields.attachments.node}
          </ComposeField>
          {error && <div className="error-box compose-error" role="alert">{error}</div>}
          <div className="task-ai-compose-action">
            <span><b>{selectedSchema?.name || "양식"}</b>에 맞춰 첨부파일, 관련 문서와 과거 업무를 참고해 지시서 전체를 채웁니다.</span>
            {(!task || (task.status === "DRAFT" && task.issuerId === session.userId)) && <button type="button" onClick={() => void generateAiDraft()} disabled={busy || aiDrafting}>
              {aiDrafting ? <LoaderCircle size={15} className="spin" /> : <Sparkles size={15} />}
              {aiDrafting ? "생성 중입니다" : aiStatus ? "AI 초안 다시 생성" : "AI 초안 생성"}
            </button>}
            {aiDrafting && <button type="button" disabled={cancelling} onClick={() => void cancelGeneration()}>{cancelling ? "취소 중…" : "생성 취소"}</button>}
          </div>
          {aiDrafting && <div className="task-ai-progress" role="status" aria-live="polite">
            <LoaderCircle size={18} className="spin" />
            <div><b>{cancelling ? "현재 작업을 마친 뒤 취소합니다" : aiProgress || "AI 초안을 생성 중입니다"}</b><span>{cancelling ? "완료된 문서 분석은 보관하고, 다음 단계는 진행하지 않습니다." : aiRequesting ? "파일 준비가 끝나면 서버에서 문서를 분석합니다." : "이 창을 닫아도 생성은 계속됩니다. 완료 후 검토해 주세요."}</span></div>
          </div>}
          {aiStatus === "SUCCEEDED" && <div className="task-ai-result">
            <b>AI 초안 적용됨</b>
            <span>폼에 생성 결과를 채웠습니다. 검토 후 저장하거나 발행하세요.</span>
            {!isCollaboration && assigneeIds.length === 0 && <p>담당자는 판단 근거가 없어 비워뒀습니다.</p>}
          </div>}
          {aiStatus === "CANCELLED" && <div className="task-ai-result" role="status">AI 초안 생성을 취소했습니다. 저장된 초안을 수정하거나 다시 생성할 수 있습니다.</div>}
          {aiStatus === "FAILED" && <div className="task-ai-result failed">
            <b>AI 초안 생성 실패</b>
            <span>{aiFailureReason || "초안을 만들지 못했습니다. 다시 생성할 수 있습니다."}</span>
          </div>}
        </section>

        <fieldset className="task-compose-inputs" disabled={aiDrafting || busy}>
        <div className="task-compose-section-title"><b>지시서 항목</b><span>{selectedSchema?.name || "양식을 먼저 선택하세요."}</span></div>
        {isCollaboration && <>
          <ComposeField label="[발신 팀]" required fieldKey="sourceDepartment" invalid={fieldInvalid("sourceDepartment")}>
            <select aria-label="발신 팀"
              value={sourceDepartmentId} onChange={event => { setSourceDepartmentId(event.target.value); setApproverId(""); }}>
              <option value="">발신 팀 선택</option>
              {sourceDepartments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
            </select>
          </ComposeField>
          {approvalRequired && <ComposeField label="[승인 매니저]" required fieldKey="approver" invalid={fieldInvalid("approver")}>
            <div className="compose-assignees">
              <UserPicker label="승인 매니저" users={approvers} value={approverId ? [approverId] : []} onChange={ids => setApproverId(ids[0] || "")} />
              <small>{approvers.length ? "선택한 매니저가 승인하면 상대 팀으로 전달됩니다." : "발신 팀에 활성 매니저가 없습니다. 조직 관리자에게 문의해 주세요."}</small>
            </div>
          </ComposeField>}
          {publishForbidden && <p className="collaboration-rejection" role="status">조직 정책상 일반 구성원은 협업 요청을 발행할 수 없습니다. 초안 저장은 가능합니다.</p>}
        </>}

        {seed?.workTitle && <ComposeField label="[Work]"><span>{seed.workTitle}</span></ComposeField>}
        {projectId && !seed?.workflowStageKey && <ComposeField label="[상위 업무]">
          <div className="compose-inline">
          <select aria-label="상위 업무" value={workItemId} disabled={!workflow || Boolean(task?.proposerId || task?.workItemId)} onChange={(event) => { setWorkItemId(event.target.value); setAssigneeIds([]); setAssigneeRecommendation(null); }}>
          <option value="">{workflow ? "상위 업무 없이" : "워크플로우 없음"}</option>
          {allowedWorkItems.map((item) => <option value={item.id} key={item.id}>{stageNameOf(item)} · {item.title}</option>)}
          </select>
          </div>
        </ComposeField>}
        <ComposeField label={isCollaboration ? "[요청할 팀]" : "[부서]"} fieldKey="department" required invalid={invalidFields.has("department") && !departmentId}>
          {isCollaboration ? <div className="collaboration-team-picker" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setTeamsOpen(false); }} onKeyDown={event => { if (event.key === "Escape" && teamsOpen) { event.stopPropagation(); setTeamsOpen(false); } }}>
            <button type="button" aria-label="요청할 팀" aria-expanded={teamsOpen} aria-controls="collaboration-team-options" onClick={() => setTeamsOpen(!teamsOpen)}>
              <span>{targetDepartmentIds.length ? targetDepartmentIds.map(id => departments.find(d => d.id === id)?.name || "선택 불가 팀").join(", ") : "팀 선택 (여러 개 선택 가능)"}</span><ChevronDown size={16} />
            </button>
            {teamsOpen && <div id="collaboration-team-options" className="collaboration-team-options" role="group" aria-label="요청할 팀 선택">
              <input aria-label="요청할 팀 검색" placeholder="팀 이름 검색" value={teamQuery} onChange={event => setTeamQuery(event.target.value)} />
              {orderedDepartments(selectableDepartments).filter(({ department }) => department.name.toLowerCase().includes(teamQuery.trim().toLowerCase())).map(({ department, depth }) => <label key={department.id} style={{ paddingLeft: 12 + depth * 14 }}>
                <input type="checkbox" checked={targetDepartmentIds.includes(department.id)} onChange={event => setTargetDepartmentIds(current => event.target.checked ? [...current, department.id] : current.filter(id => id !== department.id))} />{department.name}
              </label>)}
              {!selectableDepartments.some(d => d.name.toLowerCase().includes(teamQuery.trim().toLowerCase())) && <small>선택할 팀이 없습니다.</small>}
            </div>}
          </div> : <select aria-label="부서" value={departmentId} required disabled={Boolean(selectedWorkItem || task?.proposerId)} onChange={(event) => { setDepartmentId(event.target.value); setAssigneeIds([]); setAssigneeRecommendation(null); }}>
          <option value="">선택</option><DepartmentOptions departments={selectableDepartments} />
          </select>}
          {isCollaboration
            ? <small>선택한 팀마다 별도의 협업 요청이 만들어집니다. 각 팀의 매니저가 수락·반려하고 담당자를 지정합니다.</small>
            : !projectId && !session.roles.includes("OWNER") && <small>{selfAssignmentOnly
              ? "소속 팀에 업무를 제안할 수 있으며 승인 후 담당자는 본인으로 고정됩니다."
              : "매니저로 맡은 팀과 하위 팀에서는 담당자를 지정할 수 있습니다."}</small>}
        </ComposeField>
        {isProposal && <ComposeField label="[승인 요청할 관리자]" fieldKey="approvalManager" required invalid={invalidFields.has("approvalManager") && !approvalManagerId}>
          <UserPicker label="승인 요청할 관리자" searchLabel="승인 요청할 관리자" users={approvalManagers} value={approvalManagerId ? [approvalManagerId] : []} onChange={ids => setApprovalManagerId(ids[0] || "")} />
          <small>{approvalManagers.length ? "선택한 관리자에게 승인 요청이 전달됩니다. 같은 팀의 다른 관리자도 대신 처리할 수 있습니다." : "본인 이외의 활성 팀 관리자가 필요합니다. 관리자를 추가한 뒤 제안해 주세요."}</small>
        </ComposeField>}
        {/* 선택한 양식 순서대로 렌더한다. 설정에서 지운 항목은 이 화면에서도 사라진다. */}
        {schemaFields.filter(field => field.key !== "attachments").map((field) => {
          const custom = bespokeFields[field.key];
          return <ComposeField label={field.label} help={field.helpText} required={field.required} fieldKey={field.key} invalid={fieldInvalid(field.key)} key={field.key}>
            {custom ? custom.node : <SchemaInput field={field} value={values[field.key]} onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))} />}
          </ComposeField>;
        })}

        <ComposeField label="[성과평가]" fieldKey="evaluation" invalid={invalidFields.has("evaluation") && !countsForEvaluation && !exclusionReason.trim()}>
          <div className="compose-options">
            <label><input type="checkbox" checked={countsForEvaluation} onChange={(event) => setCountsForEvaluation(event.target.checked)} />성과평가에 반영</label>
            {!countsForEvaluation && <label className="compose-exclusion"><span>평가 제외 사유</span><input aria-label="성과평가 제외 사유" value={exclusionReason} onChange={(event) => setExclusionReason(event.target.value)} placeholder="집계에서 제외하는 이유를 적어주세요." /></label>}
          </div>
        </ComposeField>
        </fieldset>
      </div>
      <footer>
        <span>{planned ? "담당자와 기간을 지정하면 해당 단계 도달 시 발행할 업무로 예약됩니다" : isCollaboration
          ? submitMode === "draft" ? "초안은 작성자만 볼 수 있습니다" : "발행하면 선택한 팀의 매니저에게 수락 또는 반려 요청이 갑니다"
          : isProposal
            ? submitMode === "draft" ? "제안 초안은 작성자만 볼 수 있습니다" : "제안하면 소속 매니저의 준비 영역에 승인 요청이 갑니다"
            : submitMode === "draft" ? "초안은 담당자와 기간 없이 저장할 수 있으며 작성자만 볼 수 있습니다" : "발행하려면 담당자와 기간이 필요하며 담당자에게 알림이 갑니다"}</span>
        <div>
          {persistedTaskId && (!task || (task.status === "DRAFT" && task.issuerId === session.userId)) && <button type="button" className="danger-outline" title={aiDrafting ? "AI 생성 취소가 완료되면 삭제할 수 있습니다." : undefined} disabled={busy || aiDrafting || attachmentBusy} onClick={() => setDeleteConfirm(true)}><Trash2 size={14} /> 초안 삭제</button>}
          <button type="button" onClick={onClose}>{persistedTaskId || aiDrafting ? "닫기" : "취소"}</button>
          <button type="submit" value="draft" formNoValidate disabled={busy || aiDrafting}>{busy && submitMode === "draft" ? "저장 중…" : planned ? "예약 저장" : "저장"}</button>
          {!planned && <button type="submit" value="publish" className="send" disabled={busy || aiDrafting || publishForbidden}>{busy && submitMode === "publish" ? "처리 중…" : approvalRequired ? "승인 요청" : reviewingProposal ? "수정 후 승인" : task?.status === "REQUESTED" && isProposal ? "승인 요청 저장" : isProposal ? "제안" : "발행"}</button>}
        </div>
      </footer>
    </form>
  </section>
    {deleteConfirm && <ConfirmDialog title="초안을 삭제할까요?" eyebrow="DELETE DRAFT" confirmLabel="초안 삭제" busy={busy} onClose={() => setDeleteConfirm(false)} onConfirm={async () => {
      setBusy(true); setError("");
      try {
        await api.deleteTaskDraft(persistedTaskId);
        onDeleted?.(persistedTaskId);
        onClose();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "초안을 삭제하지 못했습니다."); setDeleteConfirm(false); }
      finally { setBusy(false); }
    }}><p>저장된 초안과 첨부 연결을 삭제합니다. 삭제한 초안은 복구할 수 없습니다.</p></ConfirmDialog>}
  </div>;
}

function editableCustomFields(task?: Task): Record<string, string | number | boolean> {
  return Object.fromEntries(Object.entries(task?.customFields || {}).map(([key, value]) => {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return [key, value];
    return [key, value == null ? "" : JSON.stringify(value)];
  }));
}

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function ComposeField({ label, help, required, fieldKey, invalid, children }: {
  label: string; help?: string; required?: boolean; fieldKey?: string; invalid?: boolean; children: ReactNode;
}) {
  return <div className={`task-compose-field${invalid ? " compose-invalid" : ""}`} data-compose-field={fieldKey}>
    <span>{label}{required && <i title="필수 항목">*</i>}</span>
    <div>{children}{help && <small className="field-help">{help}</small>}</div>
  </div>;
}

/**
 * 전사 스키마에만 있는 확장 항목의 입력란. 입력 타입에 따라 모양을 고른다.
 * 선택(드롭다운)은 옵션 목록 편집기가 붙기 전까지 단문 텍스트로 받는다(SETTINGS.md 3.1).
 */
function SchemaInput({ field, value, onChange }: {
  field: TaskSchemaField; value: string | number | boolean | undefined;
  onChange: (next: string | number | boolean) => void;
}) {
  const label = field.label.replace(/^\[|\]$/g, "");
  switch (field.inputType) {
    case "LONG_TEXT":
      return <textarea aria-label={label} value={String(value ?? "")} required={field.required} rows={3} onChange={(event) => onChange(event.target.value)} />;
    case "NUMBER":
      return <input aria-label={label} type="number" value={String(value ?? "")} required={field.required} onChange={(event) => onChange(event.target.value === "" ? "" : Number(event.target.value))} />;
    case "DATE_RANGE":
      return <input aria-label={label} type="date" value={String(value ?? "")} required={field.required} onChange={(event) => onChange(event.target.value)} />;
    case "CHECKBOX":
      return <label className="compose-check"><input type="checkbox" aria-label={label} checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />예</label>;
    case "FILE":
      return <p className="compose-policy">첨부는 발송 후 회차에서 추가합니다.</p>;
    default:
      return <input aria-label={label} value={String(value ?? "")} required={field.required} maxLength={2000} onChange={(event) => onChange(event.target.value)} />;
  }
}
